import {
  GenerateCommentaryField,
  generateCommentaryPage,
  schAlertTrigger,
  schAnalytics,
} from '@aloft/toolkit-shared';
import { Block, Button, Tooltip, Typography, useModal } from '@aloft/toolkit-sui';

import { ComponentProps, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { useGenerateCommentary } from '../../hooks/api/schema-hooks/useGenerateCommentary.ts';
import {
  useGetTkv2AnalyticsAttachmentAPI,
  usePutTkv2AnalyticsAttachmentAPI,
} from '../../hooks/api/toolkitComponents.ts';
import { useAlerts } from '../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectAlerts } from '../../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import {
  selectCommentary,
  selectCommentaryV2,
} from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import { processAlertTrigger, retriggerAlertToasts } from '../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import {
  mergeBasementAreaDisclosureNote,
  resolveBasementAreaDisclosureNote,
} from '../../utils/basementAreaDisclosure.ts';
import { APPRAISIFY_NAV_ALERT_BLOCK_TOOLTIP } from '../MainHeader/HeaderNav/constants.ts';
import {
  commentaryGenerationEvent,
  withCommentaryGenerationEvent,
} from './commentaryGenerationAnalytics.ts';
import { blockingCommentaryAlerts } from './generateCommentaryGate.ts';

const RECONCILIATION_COMMENTARY_FIELDS = new Set<GenerateCommentaryField>([
  'reconciliationCommentary',
  'aivReconciliationCommentary',
  'arvReconciliationCommentary',
]);

export const GenerateCommentaryButton = ({
  field,
  value,
  onGenerated,
  onGeneratingChange,
}: {
  field: GenerateCommentaryField;
  value?: string;
  onGenerated: (commentary: string) => void;
  onGeneratingChange?: (isGenerating: boolean) => void;
}) => {
  const dispatch = useAppDispatch();
  const orderID = useAppSelector(selectOrder).id;
  const alertsPerSection = useAlerts();
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const dismissedAlerts = useAppSelector(selectAlerts).dismissedAlerts ?? [];
  const finalizedExplanations = useAppSelector((state) => state.ui.finalizedExplanations);
  const explanationContext = { dismissedAlerts, finalizedExplanations };
  const blockingAlerts = blockingCommentaryAlerts(
    Object.values(alertsPerSection).flat(),
    field,
    alertsCommentary,
    explanationContext,
  );
  const blocked = blockingAlerts.length > 0;
  const { Modal, openModal, isModalOpen, closeModal } = useModal();
  const { data: analyticsResponse, isFetched: analyticsFetched } = useGetTkv2AnalyticsAttachmentAPI(
    { pathParams: { orderID: orderID ?? '' } },
    { enabled: !!orderID },
  );
  const { mutate: putAnalytics } = usePutTkv2AnalyticsAttachmentAPI();
  const { mutate, isPending } = useGenerateCommentary();

  useEffect(() => {
    onGeneratingChange?.(isPending);
  }, [isPending, onGeneratingChange]);

  useEffect(() => {
    return () => onGeneratingChange?.(false);
  }, [onGeneratingChange]);

  const record = (outcome: 'blocked' | 'succeeded' | 'failed' | 'replace-declined') => {
    if (!orderID || !analyticsFetched) return;
    const parsed = schAnalytics.safeParse(analyticsResponse?.body);
    putAnalytics({
      pathParams: { orderID },
      body: withCommentaryGenerationEvent(
        parsed.success ? parsed.data : undefined,
        commentaryGenerationEvent(field, outcome),
      ),
    });
  };

  const generate = () => {
    if (!orderID) return;
    mutate(
      { orderID, field, notes: value?.trim() || undefined },
      {
        onSuccess: (result) => {
          onGenerated(result.commentary);
          record('succeeded');
        },
        onError: () => record('failed'),
      },
    );
  };

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isPending) return;

    const trigger =
      generateCommentaryPage(field) === 'appraisify-commentary'
        ? schAlertTrigger.enum['generate/commentary']
        : schAlertTrigger.enum['generate/reconciliation'];
    dispatch(
      processAlertTrigger({
        trigger,
        alertsPerSection,
        alertsCommentary,
        explanationContext,
      }),
    );

    if (blocked) {
      dispatch(retriggerAlertToasts(blockingAlerts.map((alert) => alert.type)));
      record('blocked');
      return;
    }

    if (value?.trim()) {
      openModal();
      return;
    }

    generate();
  };

  const button = (
    <Block __dangerDoNotUseSx={blocked ? { opacity: 0.5 } : undefined}>
      <Button
        variant="secondary"
        size="sm"
        icon="sparkle"
        text={isPending ? 'Generating' : 'Generate'}
        onClick={handleClick}
      />
    </Block>
  );

  return (
    <>
      {blocked ? <Tooltip label={APPRAISIFY_NAV_ALERT_BLOCK_TOOLTIP}>{button}</Tooltip> : button}
      {isModalOpen ? (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          primaryAction={{
            text: 'Replace',
            onClick: () => {
              closeModal();
              generate();
            },
          }}
          secondaryAction={{
            text: 'Cancel',
            onClick: () => {
              closeModal();
              record('replace-declined');
            },
          }}
        >
          <Typography variant="h1">Replace commentary</Typography>
          <Typography variant="body1">
            Replace the existing commentary with AI-generated text?
          </Typography>
        </Modal>
      ) : null}
    </>
  );
};

export const FormGenerateCommentaryButton = ({
  formFieldName,
  ...props
}: Omit<ComponentProps<typeof GenerateCommentaryButton>, 'onGenerated'> & {
  formFieldName: string;
}) => {
  const { setValue } = useFormContext();
  const { hasArvOption } = useAppSelector(selectOrder);
  const { reconciliation } = useAppSelector(selectCommentary);
  return (
    <GenerateCommentaryButton
      {...props}
      onGenerated={(commentary) => {
        const disclosure = RECONCILIATION_COMMENTARY_FIELDS.has(props.field)
          ? resolveBasementAreaDisclosureNote(reconciliation, hasArvOption)
          : '';
        setValue(
          formFieldName,
          disclosure ? mergeBasementAreaDisclosureNote(commentary, disclosure) : commentary,
          {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          },
        );
      }}
    />
  );
};
